import { useState, useEffect, useCallback } from 'react'
import { cmsService } from '../../services/cmsService'
import { Field, Input, Textarea, SectionCard, PageHeader, SpinnerIcon } from '../../components/cms/shared'
import RichTextEditor from '../../components/cms/RichTextEditor'

const PAGE = 'faq'
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || 'https://tendercentrix.com'

const DEFAULT_CATEGORIES = [
    { id: 'scope', title: 'Platform Scope & Governance' },
    { id: 'coverage', title: 'Coverage & Updates' },
    { id: 'access', title: 'Access & Users' },
    { id: 'billing', title: 'Billing & Subscription' },
]

const DEFAULT_HERO = { title: 'Frequently Asked Questions', description: 'Answers to common questions about TenderCentriX platform scope, coverage, features, and pricing.' }

function initSectionState(categories) {
    const s = { hero: { saving: false, saved: false, error: null } }
    categories.forEach((cat) => { s[cat.id] = { saving: false, saved: false, error: null } })
    return s
}

export default function FaqCmsPage() {
    const [hero, setHero] = useState(DEFAULT_HERO)
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES)
    const [faqData, setFaqData] = useState({})
    const [pageLoading, setPageLoading] = useState(true)
    const [sectionState, setSectionState] = useState(initSectionState(DEFAULT_CATEGORIES))

    useEffect(() => {
        cmsService.getPage(PAGE).then((res) => {
            if (res?.data) {
                const d = res.data
                if (d.hero) setHero({ ...DEFAULT_HERO, ...d.hero })
                if (d.categories) {
                    setCategories(d.categories.map((c) => ({ id: c.id, title: c.title })))
                    const map = {}
                    d.categories.forEach((c) => { map[c.id] = c.items || [] })
                    setFaqData(map)
                }
            }
        }).catch(() => {}).finally(() => setPageLoading(false))
    }, [])

    const setSaving = (section, state) =>
        setSectionState((prev) => ({ ...prev, [section]: { ...prev[section], ...state } }))

    const save = useCallback(async (section, payload) => {
        setSaving(section, { saving: true, saved: false, error: null })
        try {
            await cmsService.updateSection(PAGE, section, payload)
            setSaving(section, { saving: false, saved: true, error: null })
            setTimeout(() => setSaving(section, { saved: false }), 3000)
        } catch (err) {
            setSaving(section, { saving: false, saved: false, error: err.message || 'Save failed' })
        }
    }, [])

    const updateItem = (catId, index, field, value) => {
        setFaqData((prev) => {
            const items = [...(prev[catId] || [])]
            items[index] = { ...items[index], [field]: value }
            return { ...prev, [catId]: items }
        })
    }

    const addItem = (catId) => {
        setFaqData((prev) => ({ ...prev, [catId]: [...(prev[catId] || []), { q: '', a: '' }] }))
    }

    const removeItem = (catId, index) => {
        setFaqData((prev) => { const items = [...(prev[catId] || [])]; items.splice(index, 1); return { ...prev, [catId]: items } })
    }

    const saveCategoryFull = (cat) => {
        save(cat.id, { id: cat.id, title: cat.title, items: faqData[cat.id] || [] })
    }

    if (pageLoading) return <div className="flex items-center justify-center py-24"><SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" /></div>

    return (
        <div className="space-y-6">
            <PageHeader title="FAQ — Content Editor" description="Edit FAQ hero and all question-answer pairs by category." liveUrl={`${FRONTEND_URL}/faq`} />

            <SectionCard title="Hero Banner" onSave={() => save('hero', hero)} {...sectionState.hero}>
                <Field label="Page Title"><Input value={hero.title} onChange={(v) => setHero((h) => ({ ...h, title: v }))} /></Field>
                <Field label="Description"><RichTextEditor value={hero.description} onChange={(v) => setHero((h) => ({ ...h, description: v }))} /></Field>
            </SectionCard>

            {categories.map((cat) => (
                <SectionCard
                    key={cat.id}
                    title={`Category: ${cat.title}`}
                    description={`${(faqData[cat.id] || []).length} Q&A pairs`}
                    onSave={() => saveCategoryFull(cat)}
                    {...(sectionState[cat.id] || {})}
                >
                    <Field label="Category Title">
                        <Input
                            value={cat.title}
                            onChange={(v) => setCategories((cs) => cs.map((c) => c.id === cat.id ? { ...c, title: v } : c))}
                        />
                    </Field>
                    <div className="space-y-4 pt-2">
                        <p className="text-xs font-bold text-admin-muted uppercase tracking-wider">Q&A Pairs</p>
                        {(faqData[cat.id] || []).map((item, i) => (
                            <div key={i} className="p-4 bg-slate-50 border border-admin-border rounded-lg space-y-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] font-bold text-admin-muted uppercase tracking-widest">Q{i + 1}</p>
                                    <button onClick={() => removeItem(cat.id, i)} className="text-[10px] text-red-400 hover:text-red-600 font-bold transition-colors">Remove</button>
                                </div>
                                <Field label="Question"><Input value={item.q} onChange={(v) => updateItem(cat.id, i, 'q', v)} /></Field>
                                <Field label="Answer"><RichTextEditor value={item.a} onChange={(v) => updateItem(cat.id, i, 'a', v)} /></Field>
                            </div>
                        ))}
                        <button
                            onClick={() => addItem(cat.id)}
                            className="w-full py-2 text-xs font-bold text-brand-navy border border-dashed border-brand-navy/40 rounded hover:bg-brand-navy/5 transition-all"
                        >
                            + Add Q&A
                        </button>
                    </div>
                </SectionCard>
            ))}
        </div>
    )
}
